import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSettings } from '@/lib/settings';
import { SITE_URL } from '@/lib/api';

export default function SEO({
    title,
    description,
    canonical,
    image,
    type = 'website',
    noindex = false,
    nofollow = false,
    schema,
}) {
    const { settings } = useSettings();
    const finalTitle = title || settings?.default_seo_title || 'Škobić Keramika';
    const finalDesc = description || settings?.default_seo_description || '';
    const rawImage = image || settings?.default_og_image || '';
    // Resolve canonical base URL with strict priority:
    // 1) REACT_APP_SITE_URL (build-time override)
    // 2) settings.canonical_base_url
    // 3) settings.site_url
    // 4) window.location.origin (runtime fallback - last resort)
    const base = (
        SITE_URL ||
        settings?.canonical_base_url ||
        settings?.site_url ||
        (typeof window !== 'undefined' ? window.location.origin : '')
    ).replace(/\/$/, '');
    // Absolutize relative og:image (e.g. /api/uploads/...) so social crawlers can fetch it.
    const finalImage = rawImage
        ? (/^(https?:)?\/\//.test(rawImage) ? rawImage : `${base}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`)
        : '';
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const finalCanonical = canonical || `${base}${path === '/' ? '' : path}`;

    const robots = `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`;

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDesc} />
            <meta name="robots" content={robots} />
            <link rel="canonical" href={finalCanonical} />
            {/* Open Graph */}
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDesc} />
            <meta property="og:type" content={type} />
            <meta property="og:url" content={finalCanonical} />
            {finalImage && <meta property="og:image" content={finalImage} />}
            <meta property="og:locale" content="hr_HR" />
            <meta property="og:site_name" content={settings?.site_name || 'Škobić Keramika'} />
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDesc} />
            {finalImage && <meta name="twitter:image" content={finalImage} />}
            {/* Theme */}
            <meta name="theme-color" content="#121212" />
            {/* Custom schema JSON-LD */}
            {schema && (
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            )}
        </Helmet>
    );
}

// Helper to build LocalBusiness schema
export function localBusinessSchema(settings) {
    return {
        '@context': 'https://schema.org',
        '@type': 'HomeAndConstructionBusiness',
        name: settings?.site_name || 'Škobić Keramika',
        image: settings?.default_og_image,
        '@id': settings?.site_url || SITE_URL,
        url: settings?.site_url || SITE_URL,
        telephone: settings?.phone || '+385 97 709 4158',
        email: settings?.email || 'skobickeramika@gmail.com',
        address: {
            '@type': 'PostalAddress',
            addressLocality: settings?.city || 'Poreč',
            addressRegion: settings?.region || 'Istra',
            addressCountry: 'HR',
            postalCode: settings?.postal_code || '52440',
        },
        areaServed: ['Poreč', 'Istra', 'Rovinj', 'Novigrad', 'Vrsar', 'Umag'],
        priceRange: '€€',
    };
}

export function organizationSchema(settings) {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: settings?.site_name || 'Škobić Keramika',
        url: settings?.site_url || SITE_URL,
        logo: settings?.logo_url,
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: settings?.phone || '+385 97 709 4158',
            contactType: 'customer service',
            areaServed: 'HR',
            availableLanguage: 'Croatian',
        },
    };
}

export function breadcrumbSchema(items) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((it, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: it.name,
            item: it.url,
        })),
    };
}

export function faqSchema(items) {
    if (!items || items.length === 0) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: { '@type': 'Answer', text: q.answer },
        })),
    };
}

export function blogPostSchema(post, settings) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt || (post.seo?.meta_description || ''),
        image: post.featured_image,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: { '@type': 'Organization', name: post.author || settings?.site_name || 'Škobić Keramika' },
        publisher: {
            '@type': 'Organization',
            name: settings?.site_name || 'Škobić Keramika',
            logo: { '@type': 'ImageObject', url: settings?.logo_url || '' },
        },
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${(settings?.site_url || SITE_URL).replace(/\/$/, '')}/blog/${post.slug}`,
        },
    };
}

export function serviceSchema(service, settings) {
    return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.title,
        description: service.short_description || service.description,
        provider: {
            '@type': 'HomeAndConstructionBusiness',
            name: settings?.site_name || 'Škobić Keramika',
            address: {
                '@type': 'PostalAddress',
                addressLocality: settings?.city || 'Poreč',
                addressRegion: settings?.region || 'Istra',
                addressCountry: 'HR',
            },
        },
        areaServed: ['Poreč', 'Istra'],
    };
}
